import { SxProps, Theme } from '@mui/material';
import { GridSlotsComponentsProps } from '@mui/x-data-grid-pro';

import {
  ATTENDANCE_CSS_CLASSES,
  ATTENDANCE_GROUP_VIEW,
  ATTENDANCE_PRIMARY_COL_FIELD,
  ATTENDANCE_ROW_HEIGHT,
  ATTENDANCE_STUDENT_VIEW,
  ATTENDANCE_SUBJECT_VIEW,
  ATTENDANCE_TRACKING_POINTS_VIEW,
  AttendanceViewConfig,
  getAttendanceDayColWidth,
} from './constants';
import { AttendanceGridColumn } from './types';

export type AttendanceDataGridProps = {
  rowHeight: number;
  columnHeaderHeight: number;
  columnsToPin: string[];
  withDataGridHover: boolean;
  noNavigationOnFirstCol: boolean;
  headerCellSelector: string;
};

const createDataGridProps = (view: AttendanceViewConfig): AttendanceDataGridProps => ({
  rowHeight: ATTENDANCE_ROW_HEIGHT,
  columnHeaderHeight: view.columnHeaderHeight,
  columnsToPin: [ATTENDANCE_PRIMARY_COL_FIELD],
  withDataGridHover: true,
  noNavigationOnFirstCol: true,
  headerCellSelector: `.${ATTENDANCE_CSS_CLASSES.dayHeaderCell}`,
});

export const ATTENDANCE_GRID_SLOT_PROPS: GridSlotsComponentsProps = {
  cell: {
    onKeyDown: (e: React.KeyboardEvent) => {
      if (e.key === 'Escape') e.stopPropagation();
    },
  },
};

export const getColWidths = (
  view: AttendanceViewConfig,
  columns: AttendanceGridColumn[],
): number[] => [view.primaryColWidth, ...columns.map(() => getAttendanceDayColWidth(view))];

export const createAttendanceGridContainerSx =
  (view: AttendanceViewConfig): SxProps<Theme> =>
  (theme: Theme) => ({
    position: 'relative',
    overflow: 'auto',
    width: '100%',
    maxWidth: '100%',
    maxHeight: '100%',
    display: 'flex',
    flexDirection: 'column',
    borderRadius: theme.spacing(1),
    backgroundColor: theme.palette.background.paper,
    '& .MuiDataGrid-pinnedRows, & .MuiDataGrid-pinnedColumns, & .MuiDataGrid-pinnedColumnHeaders': {
      backgroundColor: theme.palette.background.paper,
    },
    '.MuiStack-root.arrowLeft, .MuiStack-root.arrowRight': {
      height: view.columnHeaderHeight,
    },
    '.MuiStack-root.arrowLeft': {
      left: `${view.primaryColWidth - view.scrollArrowWidth}px !important`,
      backgroundColor: 'transparent',
      padding: theme.spacing(0, 0.75),
    },
    '.MuiStack-root.arrowRight': {
      right: 0,
      background: 'linear-gradient(90deg, transparent 0%, white 30%, white 100%)',
      padding: theme.spacing(0, 0.75),
    },
  });

export const createAttendanceDataGridSx =
  (view: AttendanceViewConfig): SxProps<Theme> =>
  (theme: Theme) => ({
    border: 'none',
    backgroundColor: theme.palette.background.paper,
    '.MuiDataGrid-columnHeaders': {
      backgroundColor: theme.palette.background.paper,
    },
    [`.${ATTENDANCE_CSS_CLASSES.primaryHeaderCell}, .${ATTENDANCE_CSS_CLASSES.primaryCell}`]: {
      borderRight: theme.mixins.borderValue(),
    },
    [`.${ATTENDANCE_CSS_CLASSES.dayHeaderCell}`]: {
      minHeight: view.columnHeaderHeight,
    },
    '.MuiDataGrid-columnHeader:focus, .MuiDataGrid-columnHeader:focus-within': {
      outline: 'none',
    },
    [`.${ATTENDANCE_CSS_CLASSES.primaryCell}`]: {
      backgroundColor: theme.palette.background.paper,
      outline: 'none',
    },
    [`.${ATTENDANCE_CSS_CLASSES.dayCell}`]: {
      padding: 0,
      outline: 'none',
      '&:focus, &:focus-within': { outline: 'none' },
    },
    '.MuiDataGrid-cell': {
      backgroundColor: theme.palette.background.paper,
      borderBottom: theme.mixins.borderValue(),
    },
    '.MuiDataGrid-cell:focus, .MuiDataGrid-cell:focus-within, .MuiDataGrid-cell:focus-visible': {
      outline: 'none',
    },
  });

const createStylingFromView = (view: AttendanceViewConfig) => ({
  view,
  primaryColWidth: view.primaryColWidth,
  gridContainerSx: createAttendanceGridContainerSx(view),
  dataGridSx: createAttendanceDataGridSx(view),
  dataGridProps: createDataGridProps(view),
  slotProps: ATTENDANCE_GRID_SLOT_PROPS,
});

export type AttendanceStyling = ReturnType<typeof createStylingFromView>;

export const ATTENDANCE_GROUP_STYLING = createStylingFromView(ATTENDANCE_GROUP_VIEW);
export const ATTENDANCE_STUDENT_STYLING = createStylingFromView(ATTENDANCE_STUDENT_VIEW);
export const ATTENDANCE_SUBJECT_STYLING = createStylingFromView(ATTENDANCE_SUBJECT_VIEW);
export const ATTENDANCE_TRACKING_POINTS_STYLING = createStylingFromView(
  ATTENDANCE_TRACKING_POINTS_VIEW,
);
